/**
 * An avatar wearing its frame.
 *
 * The frame is a CSS ring drawn just outside the picture rather than a PNG
 * overhanging it, so the avatar occupies exactly the size it was given and a
 * row does not reflow when somebody equips one.
 *
 * Kept separate from Avatar because most places - lists, chat, the roster -
 * deliberately do not draw frames: a column of them is noise.
 */

import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Avatar, PresenceDot } from './Avatar';
import { FrameRing, frameGlow as glowOf } from './Frames';

export function FramedAvatar({ user, size = 72, presence, animate, ring, radius, frameless, className }: {
  user: PublicUser;
  size?: number;
  presence?: boolean;
  animate?: boolean;
  /** A thick surface-coloured border, to lift the picture off a banner. */
  ring?: string;
  /** Corner radius. The profile header uses a square one with a border. */
  radius?: string;
  /** No ring drawn around the picture, even with a frame equipped. */
  frameless?: boolean;
  className?: string;
}) {
  const frame = !frameless && user.frame && user.frame !== 'none' ? user.frame : null;

  return (
    <span className={cn('relative inline-block flex-none leading-[0]', className)}>
      <span
        className={cn('relative block', !radius && 'rounded-[var(--avatar-radius,50%)]')}
        style={{
          ...(radius ? { borderRadius: radius } : null),
          ...(ring ? { border: '3px solid ' + ring } : null)
        }}
      >
        <Avatar user={user} size={size} presence={presence && !frame} animate={animate} radius={radius} />
        {frame ? <FrameRing frame={frame} size={size} radius={radius} /> : null}
        {/* After the ring, not before it: the ring is drawn over the picture,
            and it was drawing over the status dot along with it. */}
        {presence && frame ? <PresenceDot user={user} size={size} round={radius === '50%'} /> : null}
      </span>
    </span>
  );
}

/** The accent a frame lends to whatever it sits on, or null for no frame. */
export function frameGlow(user: Pick<PublicUser, 'frame'>): string | null {
  return glowOf(user.frame);
}
